import { OrderLabAction, ActionMaterial } from 'models/order-input/lab-order';
import { ActionGridInput, MaterialGridInput } from 'models/order-input/shared';
import { emptyMaterialInput } from './materials-data-grid/form-helper';
import { MaterialMacro, MaterialMacroOption } from 'models/material-macro';
import { PriceListMaterial } from 'models/material';

/**
 * The most a lab may charge on an action-set row, the same limit the server applies: the
 * TioTandvård price on a TioTandvård row (customer-specific first, as the grid prefills it),
 * otherwise the regular price (DD-3586).
 */
export const actionPriceLimit = (
    regularPrice: number,
    isTioTandvard: boolean | undefined,
    tiotandvardPrice?: number,
    customerSpecificTiotandvardPrice?: number,
): number => (isTioTandvard && (customerSpecificTiotandvardPrice || tiotandvardPrice)) || regularPrice;

export const convertCategorizedActionsToOrderLabActions = (categorizedActions: any[]): OrderLabAction[] => {
    const actions: OrderLabAction[] = [];
    const uniqueMap = new Map<string, boolean>();
    categorizedActions.forEach((actionCategory: any) => {
        actionCategory.actions.forEach((action: any) => {
            // Create a unique key based on action._id and subCategoryId
            const uniqueKey = `${action._id}_${actionCategory.subCategoryId}`;
            if (!uniqueMap.has(uniqueKey)) {
                uniqueMap.set(uniqueKey, true);
                actions.push({
                    _id: uniqueKey,
                    action: `${action._id}`,
                    actionCode: action.number || '',
                    internalCode: action.internalCode,
                    description: action.name || '',
                    vat: action.vat || 0,
                    basePrice: action.price || 0,
                    category: actionCategory.subCategoryName || '',
                    discountedPrice: 0, // TODO: Handle discounts???
                    price: action.price || 0,
                    tiotandvardPrice: action.tiotandvardPrice || 0,
                    quotable: action.quotable,
                    costPerTooth: Boolean(action.costPerTooth),
                    isActionMaterialRequired: action.isMaterialMandatory || false,
                    actionMaterials: extractActionMaterials(action),
                    materialMacros: action.materialMacros || [],
                });
            }
        });
    });

    return actions;
};

export const filterMaterialsByRemovedActions = (
    materials: MaterialGridInput[] = [],
    removedActionIds: string[] = [],
): MaterialGridInput[] => {
    return materials.filter((material) => {
        if (material.isActionMaterial && material.actionId && removedActionIds.includes(material.actionId)) {
            return false;
        }
        return true;
    });
};

export const createActionMaterialInput = (
    material: ActionMaterial,
    // The material takes the quantity of the action it belongs to, as a snapshot at
    // apply time - editing the action row afterwards does not move it.
    actionQuantity: string,
) => {
    const materialInput = emptyMaterialInput();
    materialInput._id = material._id;
    materialInput.description = material.name;
    materialInput.unitPrice = (material.price || 0).toString();
    materialInput.originalPrice = material.price || 0;
    materialInput.quantity = actionQuantity || '1';
    materialInput.isActionMaterial = true;
    materialInput.actionId = material.actionId;
    // The row is locked, so the grid never recomputes its totals; the order totals sum
    // them, and left at 0 they showed the order without the price difference (DD-3598).
    materialInput.priceWithoutVat = toNumber(materialInput.unitPrice) * toNumber(materialInput.quantity);
    materialInput.priceWithVat = materialInput.priceWithoutVat * (1 + toNumber(materialInput.vatPercent) / 100);
    return materialInput;
};

/**
 * The quantity the server gives a per-tooth action: the construction's teeth, at least one.
 * The lab flow sends its teeth one by one (a full jaw as the jaw with its teeth), and the
 * server counts those (DD-3598).
 */
export const perToothQuantity = (teethNumbers: number[] = []): number => new Set(teethNumbers).size || 1;

/**
 * Whether the teeth decide a row's quantity: only for a clinic action-set action marked "cost
 * per tooth", whose quantity the server ignores. A row from the lab's own price list keeps the
 * quantity the lab enters, even when the server flagged it per tooth because the construction
 * is made tooth by tooth (DD-3766).
 */
export const quantityFollowsTeeth = (action: { costPerTooth?: boolean }, usesClinicActionSet: boolean): boolean =>
    usesClinicActionSet && Boolean(action.costPerTooth);

/**
 * Per-tooth rows recounted for the construction's teeth, with their totals. The server
 * recounts them on save anyway, so the grid and the order totals show what will be stored.
 */
export const recountPerToothActions = (
    actions: ActionGridInput[] = [],
    quantity: number,
    usesClinicActionSet: boolean,
): ActionGridInput[] =>
    actions.map((action) => {
        if (!quantityFollowsTeeth(action, usesClinicActionSet) || toNumber(action.quantity) === quantity) {
            return action;
        }
        const priceWithoutVat = toNumber(action.unitPrice) * quantity;
        return {
            ...action,
            quantity: String(quantity),
            priceWithoutVat,
            priceWithVat: priceWithoutVat * (1 + toNumber(action.vatPercent) / 100),
        };
    });

const extractActionMaterials = (action: any): ActionMaterial[] => {
    if (action.isMaterialMandatory && action.actionMaterials.length > 0) {
        return action.actionMaterials.map((material: any) => ({
            _id: material.material,
            actionId: action._id,
            name: material.materialName,
            price: material.priceDiff,
        }));
    }
    return [];
};

const changedActionQuantitiesByActionId = (
    previousActions: ActionGridInput[],
    updatedActions: ActionGridInput[],
): Map<string, string> => {
    const changedQuantities = new Map<string, string>();
    updatedActions.forEach((updatedAction) => {
        if (!updatedAction.actionId) {
            return;
        }
        const previousAction = previousActions.find(
            (candidate) => candidate.actionId === updatedAction.actionId && candidate._id === updatedAction._id,
        );
        if (previousAction && previousAction.quantity !== updatedAction.quantity) {
            changedQuantities.set(updatedAction.actionId, updatedAction.quantity);
        }
    });
    return changedQuantities;
};

/**
 * Keeps a material's quantity in step with the product it was added for. Both pickers
 * fire the moment the action is selected, before the lab has reached the quantity column,
 * so the quantity captured then is almost always 1 - this re-syncs it when the action's
 * quantity is edited afterwards.
 *
 * Covers both kinds of linked row: actionMaterials (`isActionMaterial` true) and macro
 * materials, which carry `actionId` alone with `isActionMaterial` left false so they stay
 * editable and deletable. A manual edit on the material row survives until the lab
 * changes that action's quantity again.
 */
export const syncLinkedMaterialQuantities = (
    materials: MaterialGridInput[] = [],
    previousActions: ActionGridInput[] = [],
    updatedActions: ActionGridInput[] = [],
): MaterialGridInput[] => {
    const changedQuantities = changedActionQuantitiesByActionId(previousActions, updatedActions);
    if (changedQuantities.size === 0) {
        return materials;
    }
    return materials.map((material) => {
        if (!material.actionId) {
            return material;
        }
        const actionQuantity = changedQuantities.get(material.actionId);
        if (actionQuantity === undefined || actionQuantity === material.quantity) {
            return material;
        }
        // Totals are recomputed here as well as by the grid row, so the stored values
        // never lag behind the quantity they were derived from.
        const priceWithoutVat = toNumber(material.unitPrice) * toNumber(actionQuantity);
        return {
            ...material,
            quantity: actionQuantity,
            priceWithoutVat,
            priceWithVat: priceWithoutVat * (1 + toNumber(material.vatPercent) / 100),
        };
    });
};

/**
 * Classifies every macro linked to a product rather than dropping the unusable ones, so the
 * picker can show *why* one cannot be applied instead of silently omitting it. A macro is
 * only selectable when the client's material price list carries every material it holds -
 * a missing material would otherwise be added with no price.
 */
export const toMaterialMacroOptions = (
    macros: MaterialMacro[] = [],
    priceListMaterials: PriceListMaterial[] = [],
): MaterialMacroOption[] => {
    const priceListMaterialIds = new Set(priceListMaterials.map((material) => material.material as string));
    return macros.map((macro) => {
        const missingMaterials = (macro.materials || []).filter((macroMaterial) => {
            const materialId = macroMaterial.material?._id;
            return !materialId || !priceListMaterialIds.has(materialId);
        });
        return {
            macro,
            isSelectable: missingMaterials.length === 0,
            // Derived from missingMaterials, not the other way round - a material with
            // neither code nor description still counts as missing, it just has no label.
            missingMaterialNames: missingMaterials
                .map((macroMaterial) =>
                    [macroMaterial.material?.materialCode, macroMaterial.material?.description]
                        .filter(Boolean)
                        .join(' - '),
                )
                .filter(Boolean),
        };
    });
};

/**
 * The material grid stores the *catalog* material id in a row's `_id`, which is the
 * price list's `material` field - not its own `_id`. Match either, so a caller holding
 * a price-list row id cannot silently miss.
 */
export const findPriceListMaterial = (
    priceListMaterials: PriceListMaterial[] = [],
    materialId?: string,
): PriceListMaterial | undefined => {
    if (!materialId) {
        return undefined;
    }
    return priceListMaterials.find(
        (priceListMaterial) => priceListMaterial.material === materialId || priceListMaterial._id === materialId,
    );
};

/**
 * A batch-tracked material has to name the batch it came from. Only enforced while an
 * open batch exists to pick - otherwise a user without batch-management rights, who
 * cannot open one, would be stuck on an unsatisfiable row.
 */
export const requiresBatchSelection = (priceListMaterials: PriceListMaterial[] = [], materialId?: string): boolean => {
    const priceListMaterial = findPriceListMaterial(priceListMaterials, materialId);
    if (!priceListMaterial?.batchInventoryManagement) {
        return false;
    }
    return (priceListMaterial.batches || []).some((batch) => !batch.closed);
};

export const createMacroMaterialInputs = (
    macro: MaterialMacro,
    priceListMaterials: PriceListMaterial[],
    // A macro is a named set of materials with no quantity of its own - each material
    // takes the quantity of the action that pulled the macro in.
    actionQuantity: string,
    // Only used in-session, to let syncLinkedMaterialQuantities follow that action's
    // quantity. Never persisted - see the note on isActionMaterial below.
    actionId?: string,
): MaterialGridInput[] => {
    return (macro.materials || []).map((macroMaterial) => {
        const priceListMaterial = findPriceListMaterial(priceListMaterials, macroMaterial.material?._id);
        const materialInput = emptyMaterialInput();
        materialInput._id = macroMaterial.material?._id || '';
        materialInput.materialCode = priceListMaterial?.materialCode || macroMaterial.material?.materialCode || '';
        materialInput.description = priceListMaterial?.description || macroMaterial.material?.description || '';
        materialInput.quantity = actionQuantity || '1';
        materialInput.basePrice = Number(priceListMaterial?.basePrice) || 0;
        materialInput.unitPrice = priceListMaterial?.price?.toString() || '';
        materialInput.vatPercent = priceListMaterial?.vat?.toString() || '0';
        materialInput.priceWithoutVat = Number(priceListMaterial?.price) || 0;
        materialInput.priceWithVat = priceListMaterial?.vat
            ? Number(priceListMaterial.price) * (1 + priceListMaterial.vat / 100)
            : Number(priceListMaterial?.price) || 0;
        materialInput.originalPrice = Number(priceListMaterial?.basePrice) || 0;
        materialInput.originalVat = priceListMaterial?.vat || 0;
        // Macro materials are plain, fully editable material rows. isActionMaterial /
        // materialActionId belong to the separate actionMaterial concept, where they
        // lock the row down (read-only price/vat/material, no delete) - a macro is only
        // a starting point the lab must stay free to adjust, so isActionMaterial stays
        // false. actionId on its own is inert: every consumer gates on isActionMaterial,
        // including convertMaterialGridToMaterials, so it is never sent to the backend
        // and the row is never auto-removed with its action.
        materialInput.actionId = actionId;
        // Deliberately left empty, even for batch-tracked materials - the user must
        // pick the correct batch manually since a macro cannot know which one applies.
        materialInput.batchNumber = '';
        // ...which only holds if the row refuses to pass validation until they do. The
        // grid derives its own validity from the stored rows, so a row that never gets
        // touched would otherwise sail through with no batch.
        materialInput.isValid = !requiresBatchSelection(priceListMaterials, materialInput._id);
        return materialInput;
    });
};

export const toNumber = (v: unknown) => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
        const n = parseFloat(v.trim().replace(',', '.'));
        return isNaN(n) ? 0 : n;
    }
    return 0;
};
